// ==============================
// Imports
// ==============================

// MUI Components
import AppBarSpacer from '../components/common/AppBarSpacer';
import { Box, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { PersonAddAlt1, Domain, Person, Groups } from '@mui/icons-material';
import PageHeader from '../components/common/PageHeader';

// Libraries
import { useState } from 'react';

// Local components
import MiniDrawer from '../components/MiniDrawer';
import UserTable from '../components/UserTable';
import UserGroupView from '../components/UserGroupView';
import QuickActions from '../components/QuickActions';
import { useTranslation } from '../i18n';

type UserView = 'individual' | 'group';

const ClientLayout = () => {
  const { t } = useTranslation();
  // States
  const [open, setOpen] = useState(true);
  const [userView, setUserView] = useState<UserView>('individual');

  // ==============================
  // Render
  // ==============================
  return (
    <>
      <div className="flex h-screen">
        {/* Sidebar */}
        <MiniDrawer open={open} setOpen={setOpen} />

        {/* Main content */}
        <main className="flex-1 overflow-auto app-page-bg">
          {/* Offset for fixed AppBar (height = 64px) */}
          <AppBarSpacer />

          <div className="h-full px-6 py-4">
            <PageHeader
              title={t('user.pageTitle')}
              subtitle={t('user.pageSubtitle')}
            />
            {/* One row: view tabs on the left, add buttons on the right */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                mb: 2,
              }}
            >
              {/* View tabs — Individual (current table) vs Group (duplicates) */}
              <ToggleButtonGroup
                value={userView}
                exclusive
                onChange={(_, value: UserView | null) => {
                  if (value) setUserView(value);
                }}
                size="small"
                aria-label="user management view mode"
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  '& .MuiToggleButton-root': { flex: { xs: 1, sm: 'none' } },
                }}
              >
                <ToggleButton
                  value="individual"
                  aria-label="individual view"
                  sx={{ p: 1.5 }}
                >
                  <Person sx={{ mr: 0.5 }} />
                  {t('user.individual')}
                </ToggleButton>
                <ToggleButton
                  value="group"
                  aria-label="group view"
                  sx={{ p: 1.5 }}
                >
                  <Groups sx={{ mr: 0.5 }} />
                  {t('user.group')}
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Quick-access buttons (same behavior as the Home cards) */}
              <QuickActions>
                {(handlers) => (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PersonAddAlt1 />}
                      onClick={handlers.openNewUser}
                      className="bg-blue-600 hover:bg-blue-700 !text-white font-semibold py-2 px-4 rounded"
                    >
                      {t('user.addNewUser')}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<Domain />}
                      onClick={handlers.openNewOrg}
                      className="bg-blue-600 hover:bg-blue-700 !text-white font-semibold py-2 px-4 rounded"
                    >
                      {t('user.addNewOrg')}
                    </Button>
                  </Box>
                )}
              </QuickActions>
            </Box>

            {/* User table / group view */}
            {userView === 'individual' ? <UserTable /> : <UserGroupView />}
          </div>
        </main>
      </div>
    </>
  );
};

export default ClientLayout;
